/* eslint-disable max-lines */
import { NotificationTypeEnum } from 'configs/constants/enums/notification'
import debounce from 'lodash/debounce'
import escapeRegExp from 'lodash/escapeRegExp'
import set from 'lodash/set'
import trim from 'lodash/trim'
import { makeAutoObservable } from 'mobx'
import { RootStore } from 'stores'
import { IFilter, Where } from 'types/common'
import { AggregationPipeline } from 'types/common/aggregation'
import { getGroupsProcesses } from 'API/groupProcesses'
import { getNotifications } from 'API/notification'
import { INotificationWithRelations } from 'interfaces/notification'
import {
  aggregateCountProcessSteps,
  aggregateCountProcesses,
  countProcesses as countProcessesAPI,
  getProcessStepListAPI,
  getProcessById,
  getAllProcesses,
  aggregateProcessesSearch,
  getDetailProcessById
} from 'API/process'
import { updateStepBatch } from 'API/step'
import { ProcessList } from 'constants/process'
import { IProcess, IProcessWithRelations, IProcessesFilterForm } from 'interfaces/process'
import { IStep, IStepWithRelations } from 'interfaces/step'
import { ITextContentWithRelations } from 'interfaces/textContent'
import { getValidArray } from 'utils/common'

class ProcessStore {
  rootStore: RootStore

  process: IProcessWithRelations = {} as IProcessWithRelations
  stepsNeedUpdate: IStep[] = []

  processes: IProcessWithRelations[] = []

  processList: IProcessWithRelations[] = []

  processesFilter: IProcessesFilterForm = {
    collections: [],
    creators: [],
    groups: [],
    sort: ''
  }

  numberOfProcesses: number = 0

  numberOfDrafts: number = 0 // INFO: for action sheet render only

  selectedProcessId: number = -1

  selectedProcess: IProcessWithRelations = {} as IProcessWithRelations

  processDetail: IProcessWithRelations = {} as IProcessWithRelations

  openProcessDetailPanel: boolean = false

  openInformationModal: boolean = false

  openCommentModal: boolean = false

  expandingSteps: number[] = []

  isFetchingList: boolean = false

  timeFetchingList: number = new Date().getTime()

  isFetchingTotalNumber: boolean = false

  newProcessGroupIds: number[] = []

  isFetchingListSteps: boolean = false

  processSteps: IStep[] = []

  totalStepsProcess: number = 0

  isSearching: boolean | null = null

  isGroupProcessesLoading = false

  isManageModeInDetail = false
  canUserEditInProcessDetail = false

  isManageModeInArchive = false

  isManageModeInProcessList = false
  canUserEditInProcessList = false

  commonStepIdsNeedToUpdate: number[] = []

  constructor(rootStore: RootStore) {
    makeAutoObservable(this)

    this.rootStore = rootStore
  }

  public setManageModeInDetail(isManageMode: boolean) {
    this.isManageModeInDetail = isManageMode ?? false
  }

  public setManageModeInList(isManageMode: boolean) {
    this.isManageModeInProcessList = isManageMode ?? false
  }

  public setManageModeInArchive(isManageMode: boolean) {
    this.isManageModeInArchive = isManageMode ?? false
  }

  public setCanUserEditInProcessDetail(canEdit: boolean) {
    this.canUserEditInProcessDetail = canEdit
  }

  public setCanUserEditInProcessList(canEdit: boolean) {
    this.canUserEditInProcessList = canEdit
  }

  public setSelectedProcessId(processId: number): void {
    this.selectedProcessId = processId
  }

  public async setSelectedSharedGroupOfProcess(processId: number): Promise<void> {
    this.isGroupProcessesLoading = true
    const groupProcesses = await getGroupsProcesses({ where: { processId } })
    this.setNewProcessGroupIds(groupProcesses.map(groupProcess => groupProcess.groupId))
    this.isGroupProcessesLoading = false
  }

  public setSelectedProcess(process: IProcessWithRelations): void {
    this.selectedProcess = process
  }

  public async getProcessDetail(processId: number, filter: IFilter<IProcess> = {}): Promise<void> {
    const process = await getProcessById(processId, filter)
    this.process = process
    this.selectedProcess = process
    this.selectedProcessId = processId

    // Auxiliary data, deliberately not awaited. A failed notifications fetch must
    // not reject the whole detail load — the page would misreport it as "not
    // allowed" and redirect even though the drill itself loaded fine — and every
    // editor save re-reads the process, so blocking on a second round trip made
    // each of them visibly slower for a badge nobody is waiting on.
    getNotifications({
      where: { type: NotificationTypeEnum.UPDATED_COMMON_STEP_NOTIFICATION, processId }
    })
      .catch((): INotificationWithRelations[] => [])
      .then(notifications => {
        this.commonStepIdsNeedToUpdate = getValidArray(notifications).map(notification => notification.stepId) ?? []
      })
  }

  public async setProcessStepPosition(
    temporarySteps: IStepWithRelations[],
    startIndex: number,
    endIndex: number
  ): Promise<void> {
    const [moved] = temporarySteps.splice(startIndex, 1)
    temporarySteps.splice(endIndex, 0, moved)
    const updatedSteps = temporarySteps.map((step, index) => ({ ...step, position: index + 1 }))
    await updateStepBatch(updatedSteps)
    this.process.steps = updatedSteps
  }

  public async getProcessByAggregate(
    dataQuery: AggregationPipeline = [],
    customParser: (response: unknown) => IProcessWithRelations[]
  ): Promise<void> {
    const currentTime: number = new Date().getTime()
    if (currentTime > this.timeFetchingList) {
      this.timeFetchingList = currentTime
    }
    this.isFetchingList = true
    const responseData = await aggregateProcessesSearch(dataQuery, currentTime)
    if (this.timeFetchingList === currentTime) {
      this.processes = customParser(responseData?.paginatedResults ?? [])
      this.numberOfProcesses = responseData?.totalResults ?? 0
    }
    this.isFetchingList = false
  }

  public resetProcessList(): void {
    this.processes = []
    this.numberOfProcesses = 0
  }

  public async countProcesses(where: Where<IProcess> = {}): Promise<void> {
    this.numberOfProcesses = await countProcessesAPI(where)
  }

  public async countDrafts(countQuery: AggregationPipeline = []): Promise<void> {
    const countResponse = await aggregateCountProcesses(countQuery)
    this.numberOfDrafts = countResponse
  }

  public setOpenInformationModal(status: boolean): void {
    this.openInformationModal = status
  }

  public setOpenCommentModal(status: boolean): void {
    this.openCommentModal = status
  }

  public setExpandingSteps(expandingSteps: number[]): void {
    this.expandingSteps = expandingSteps
  }

  public setOpenProcessDetailPanel(status: boolean): void {
    this.openProcessDetailPanel = status
  }

  public setProcessesFilter(processesFilter: IProcessesFilterForm): void {
    this.processesFilter = { ...this.processesFilter, ...processesFilter }
  }

  public setNewProcessGroupIds(newProcessGroupIds: number[]): void {
    this.newProcessGroupIds = newProcessGroupIds
  }

  public async getListStepsProcess(processId: number, offset: number, limit: number): Promise<void> {
    const responseData = await getProcessStepListAPI({
      limit: limit,
      offset: offset,
      where: {
        processId: processId
      },
      order: 'position ASC'
    })
    this.processSteps = responseData
  }

  /**
   * Apply an accepted reorder to the loaded Stack instead of re-reading it.
   *
   * A reorder changes nothing but `position`, so a full detail read (process +
   * every relation) just to learn what the client already knows is the slowest
   * part of the interaction. Callers hold the previous order and restore it if
   * the write is rejected.
   */
  public applyStepOrder(orderedIds: number[]): void {
    const steps = getValidArray(this.process?.steps)
    const byId = new Map(steps.map(step => [Number(step.id), step]))
    const ordered = orderedIds.map(id => byId.get(id)).filter(Boolean) as IStepWithRelations[]
    const missing = steps.filter(step => !orderedIds.includes(Number(step.id)))
    this.process.steps = [...ordered, ...missing].map((step, index) => ({ ...step, position: index + 1 }))
  }

  /**
   * Insert a just-created Instruction into the loaded Drill.
   *
   * The create call returns the row, so a full detail read afterwards only tells
   * the client what it already has — and that read is what made adding an
   * Instruction feel slow next to adding a Requirement.
   */
  public addTextContentToStep(stepId: number, textContent: ITextContentWithRelations): void {
    this.process.steps = getValidArray(this.process?.steps).map(step => {
      if (Number(step.id) !== Number(stepId)) return step
      const contents = [...getValidArray(step.textContents), textContent]
      return {
        ...step,
        textContents: contents.sort((first, second) => Number(first?.position ?? 0) - Number(second?.position ?? 0))
      }
    })
  }

  /** The same, for one Drill's Instructions. */
  public applyTextContentOrder(stepId: number, orderedIds: string[]): void {
    this.process.steps = getValidArray(this.process?.steps).map(step => {
      if (Number(step.id) !== Number(stepId)) return step
      const contents = getValidArray(step.textContents)
      const byId = new Map(contents.map(content => [String(content.id), content]))
      const ordered = orderedIds.map(id => byId.get(id)).filter(Boolean) as ITextContentWithRelations[]
      const missing = contents.filter(content => !orderedIds.includes(String(content.id)))
      return {
        ...step,
        textContents: [...ordered, ...missing].map((content, index) => ({ ...content, position: index + 1 }))
      }
    })
  }

  public resetProcessSteps(): void {
    this.processSteps = []
  }

  public async getProcessDetailById(processId: number): Promise<IProcessWithRelations> {
    const processDetail = await getDetailProcessById(processId)
    this.processDetail = processDetail
    return processDetail
  }

  public async aggregateCountStepsProcess(processId: number): Promise<void> {
    const responseData = await aggregateCountProcessSteps({
      processId: processId
    })
    this.totalStepsProcess = responseData
  }

  public async getAllProcessList(offset: number, keyword: string = ''): Promise<void> {
    const organizationId = this.rootStore.authStore.activeOrgId ?? 0
    const filter: IFilter<IProcess> = {
      where: {
        organizationId,
        name: {
          like: `.*${trim(keyword)}`,
          options: 'i'
        }
      },
      offset: offset,
      limit: ProcessList.LIMIT,
      order: ['updatedAt DESC'],
      include: [{ relation: 'steps' }]
    }
    const responseData = await getAllProcesses(filter)
    this.processList = [...this.processList, ...responseData]
  }

  public async search(
    keyword: string,
    offset: number,
    tagIds?: number[],
    creatorIds?: number[],
    limit?: number
  ): Promise<void> {
    const searchKeyword: string = escapeRegExp(trim(keyword))
    const organizationId = this.rootStore.authStore.activeOrgId ?? 0
    try {
      const filter = {
        offset,
        limit: limit ?? ProcessList.LIMIT,
        where: {
          isPublished: true,
          organizationId,
          name: {
            like: `.*${searchKeyword}`,
            options: 'i'
          },
          $or: [{ archivedAt: { $exists: false } }, { archivedAt: { $eq: null } }]
        },
        order: ['updatedAt DESC'],
        include: [
          // An archived or deleted drill is not a search result: it is gone from
          // its own stack, so it must not be offered anywhere it can be picked —
          // the related-content destination picker, the plan builder library, the
          // legacy external-step modal. The server does not drop these rows on its
          // own, so the scope has to say so here rather than leaving each screen to
          // filter what it was sent.
          { relation: 'steps', scope: { where: { archived: { neq: true }, isDeleted: { neq: true } } } },
          { relation: 'tags' },
          { relation: 'creator' }
        ]
      }

      if (creatorIds?.length) {
        // creatorIds now carry OrgMembership ids — match on ownerMembershipId
        set(filter, 'where.ownerMembershipId', { inq: creatorIds })
      }
      const responseData = await getAllProcesses(filter)
      if (tagIds?.length) {
        const filteredProcesses = responseData.filter(process =>
          process.tags?.some(tag => tagIds.includes(tag.id ?? 0))
        )
        this.processList = filteredProcesses
        return
      }

      this.processList = responseData
    } catch (error: any) {
      console.log('search Log ~ error', error)
    } finally {
      this.isSearching = false
    }
  }

  debouncedSearch = debounce(this.search, 700)

  public changeSearchText(searchText: string, offset?: number): void {
    this.isSearching = true
    this.debouncedSearch(searchText, offset ?? 0)
  }

  /**
   * Clear org-scoped process state. Called on active-profile switch so a Coach
   * profile never sees a Player profile's cached processes (and vice versa).
   * Resets list/detail/selection caches back to their constructor defaults.
   */
  public reset(): void {
    this.process = {} as IProcessWithRelations
    this.stepsNeedUpdate = []
    this.processes = []
    this.processList = []
    this.processesFilter = { collections: [], creators: [], groups: [], sort: '' }
    this.numberOfProcesses = 0
    this.numberOfDrafts = 0
    this.selectedProcessId = -1
    this.selectedProcess = {} as IProcessWithRelations
    this.processDetail = {} as IProcessWithRelations
    this.expandingSteps = []
    this.processSteps = []
    this.totalStepsProcess = 0
    this.newProcessGroupIds = []
    this.commonStepIdsNeedToUpdate = []
  }
}

export default ProcessStore
